import { FeatureGate } from '../components/FeatureGate';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import {
  deletePrototype,
  fetchPrototype,
  fetchWorkspaceEntitlements,
} from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { useCapabilities } from '../hooks/useCapabilities';
import { useOrg } from '../org/useOrg';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CustomDomainsCard } from '../components/CustomDomainsCard';
import { PageHeader } from '../components/PageHeader';
import { ProjectSectionNav } from '../components/ProjectSectionNav';
import { ProjectTransferZone } from '../components/ProjectTransferZone';
import { PrototypeDesignCard } from '../components/PrototypeDesignCard';
import { PrototypeFeedbackCard } from '../components/PrototypeFeedbackCard';
import { PrototypeResourceLimitsCard } from '../components/PrototypeResourceLimitsCard';
import { PrototypeSecretsCard } from '../components/PrototypeSecretsCard';
import { PrototypeAdminAccountCard } from '../components/PrototypeAdminAccountCard';
import { PrototypeStorageCard } from '../components/PrototypeStorageCard';
import { PrototypeProductionCard } from '../components/PrototypeProductionCard';
import { SectionDivider } from '../components/SectionDivider';
import { ErrorState, LoadingState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export function PrototypeSettingsPage() {
  const { projectId = '' } = useParams();
  const { wp } = useWorkspacePath();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, isWorkspaceOwner } = useCapabilities();
  const canManage = can('prototype:manage');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { activeOrg } = useOrg();

  const protoQuery = useQuery({
    queryKey: qk.prototype(projectId),
    queryFn: () => fetchPrototype(projectId),
    enabled: !!projectId,
  });

  useCanonicalProjectRef(projectId, protoQuery.data?.code);
  const proto = protoQuery.data;

  const entitlementsQuery = useQuery({
    queryKey: ['orgEntitlements', activeOrg?.id],
    queryFn: () => fetchWorkspaceEntitlements(activeOrg!.id),
    enabled: !!activeOrg?.id,
  });
  const canEditResourceLimits =
    entitlementsQuery.data?.features.resourceLimitOverrides.enabled === true;

  useDocumentTitle(proto ? `Settings · ${proto.name}` : 'Settings');

  const deleteMutation = useMutation({
    mutationFn: () => deletePrototype(proto!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.prototypes });
      navigate(wp('/prototypes'));
    },
  });

  if (protoQuery.isLoading) return <LoadingState label="Loading settings…" />;
  if (protoQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(protoQuery.error, 'Failed to load prototype')}
        onRetry={() => protoQuery.refetch()}
      />
    );
  }

  const ref = proto!.code ?? proto!.id;
  const { deployment } = proto!;

  return (
    <div className="space-y-6 pb-16 sm:pb-0">
      <Breadcrumbs
        items={[
          { label: 'Prototypes', to: wp('/prototypes') },
          { label: proto!.name, to: wp(`/prototypes/${ref}`) },
          { label: 'Settings' },
        ]}
      />

      <PageHeader
        title="Settings"
        subtitle={`Configuration for ${proto!.name}.`}
      />

      <ProjectSectionNav
        kind="prototype"
        projectRef={ref}
        active="settings"
      />

      {/* Contributors may flip it too; viewers see it read-only. */}
      <PrototypeProductionCard
        prototype={proto!}
        canOperate={can('prototype:operate', { projectId: proto!.id })}
      />

      {canManage ? (
        <>
          <PrototypeDesignCard prototype={proto!} />

          <FeatureGate feature="prototypeCustomDomains">
            <CustomDomainsCard
              prototypeId={proto!.id}
              cnameTarget={deployment.platformUrl.replace(/^https?:\/\//, '')}
              domains={deployment.domains ?? []}
            />
          </FeatureGate>

          {proto!.factory && <PrototypeAdminAccountCard prototypeId={proto!.id} />}

          <PrototypeSecretsCard prototypeId={proto!.id} />

          <PrototypeStorageCard prototype={proto!} />

          <PrototypeFeedbackCard prototype={proto!} />

          {canEditResourceLimits && <PrototypeResourceLimitsCard prototype={proto!} />}

          <SectionDivider label="Danger zone" tone="danger" />

          {/* Transfer is for owners of the source workspace only. */}
          {isWorkspaceOwner && (
            <ProjectTransferZone
              project={{
                id: proto!.id,
                name: proto!.name,
                kind: 'prototype',
                ref,
                factory: proto!.factory,
              }}
            />
          )}

          <div className="card flex flex-wrap items-center justify-between gap-4 p-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">Delete this prototype</p>
              <p className="text-xs text-ink-faint">
                Permanently removes the live container, the GitHub repo, and all run history. This cannot be undone.
              </p>
            </div>
            <button
              type="button"
              className="btn-danger-ghost"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              Delete
            </button>
          </div>
        </>
      ) : (
        <p className="text-sm text-ink-faint">
          You do not have permission to manage this prototype's settings.
        </p>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete prototype?"
        confirmLabel="Delete prototype"
        busy={deleteMutation.isPending}
        error={
          deleteMutation.isError
            ? errorMessage(deleteMutation.error, 'Could not delete prototype')
            : null
        }
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setConfirmDelete(false)}
        message={
          <>
            Delete{' '}
            <span className="font-medium text-ink">{proto!.name}</span>? This permanently removes{' '}
            <span className="font-medium">the live container, the GitHub repo, and all run history</span>. This cannot be undone.
          </>
        }
      />
    </div>
  );
}
